import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

interface SubscriptionState {
  isPremium: boolean;
  isTrialActive: boolean;
  trialDaysLeft: number;
  planType: 'trial' | 'premium' | 'free' | 'family';
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  lsStatus: string | null;
  lsSubscriptionId: string | null;
  lsVariantId: string | null;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  hadTrial: boolean;
  loading: boolean;
  fetchError: boolean;
}

const REFETCH_DEBOUNCE_MS = 5000;

export function useSubscription() {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [state, setState] = useState<SubscriptionState>({
    isPremium: false,
    isTrialActive: false,
    trialDaysLeft: 0,
    planType: 'free',
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
    lsStatus: null,
    lsSubscriptionId: null,
    lsVariantId: null,
    trialStartedAt: null,
    trialEndsAt: null,
    hadTrial: false,
    loading: true,
    fetchError: false,
  });

  const lastFetchAtRef = useRef<number>(0);

  const processSubscription = useCallback((data: any) => {
    const now = new Date();
    const trialEndsAt = new Date(data.trial_ends_at);
    const diffMs = trialEndsAt.getTime() - now.getTime();
    const trialDaysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

    let isPremium = false;
    let isTrialActive = false;
    let planType: 'trial' | 'premium' | 'free' | 'family' = 'free';

    if (data.plan_type === 'family' && data.subscription_status === 'active') {
      isPremium = true;
      planType = 'family';
    } else if (data.plan_type === 'premium' && data.subscription_status === 'active') {
      isPremium = true;
      planType = 'premium';
    } else if (data.plan_type === 'trial' && trialDaysLeft > 0) {
      isPremium = true;
      isTrialActive = true;
      planType = 'trial';
    } else {
      planType = 'free';
    }

    setState({
      isPremium,
      isTrialActive,
      trialDaysLeft,
      planType,
      cancelAtPeriodEnd: !!data.cancel_at_period_end,
      currentPeriodEnd: data.current_period_end || null,
      lsStatus: data.ls_status || null,
      lsSubscriptionId: data.ls_subscription_id || null,
      lsVariantId: data.ls_variant_id ? String(data.ls_variant_id) : null,
      trialStartedAt: data.trial_started_at || null,
      trialEndsAt: data.trial_ends_at || null,
      hadTrial: !!data.trial_started_at,
      loading: false,
      fetchError: false,
    });
  }, []);

  // Check whether the user has an inherited family entitlement via an owner
  // whose family plan is active. Returns the owner's current_period_end so
  // downstream UI knows when the seat lapses.
  const fetchFamilyEntitlement = useCallback(async (): Promise<{
    active: boolean; ownerId: string | null; currentPeriodEnd: string | null;
  }> => {
    if (!user) return { active: false, ownerId: null, currentPeriodEnd: null };
    const { data, error } = await supabase.rpc('get_family_entitlement', {
      _user_id: user.id,
    });
    if (error) {
      console.warn('get_family_entitlement error:', error.message);
      return { active: false, ownerId: null, currentPeriodEnd: null };
    }
    const row = Array.isArray(data) ? data[0] : (data as any);
    if (row?.is_active) {
      return {
        active: true,
        ownerId: row.owner_id ?? null,
        currentPeriodEnd: row.current_period_end ?? null,
      };
    }
    return { active: false, ownerId: null, currentPeriodEnd: null };
  }, [user]);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    lastFetchAtRef.current = Date.now();

    try {
      const [subResult, familyEnt] = await Promise.all([
        supabase.from('user_subscriptions').select('*').eq('user_id', user.id).maybeSingle(),
        fetchFamilyEntitlement(),
      ]);
      const { data, error } = subResult;

      if (error) throw error;

      let baseRow: any = data;
      if (!baseRow) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke('manage-subscription', {
          body: { action: 'ensure_subscription' },
        });

        if (fnError || !fnData?.data) {
          console.error('Error creating subscription:', fnError);
          setState(prev => ({ ...prev, loading: false, fetchError: true }));
          return;
        }
        baseRow = fnData.data;
      }

      // If the user's own subscription is not active premium/family, but they
      // are a member of a paying family owner's group, elevate them to family.
      const ownActive =
        (baseRow.plan_type === 'premium' || baseRow.plan_type === 'family') &&
        baseRow.subscription_status === 'active';

      if (!ownActive && familyEnt.active) {
        baseRow = {
          ...baseRow,
          plan_type: 'family',
          subscription_status: 'active',
          current_period_end: familyEnt.currentPeriodEnd,
        };
      }

      processSubscription(baseRow);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setState(prev => ({ ...prev, loading: false, fetchError: true }));
    }
  }, [user, processSubscription, fetchFamilyEntitlement]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Refetch on window focus / tab visibility - catches changes made on LS site or other tabs.
  useEffect(() => {
    if (!user) return;

    const maybeRefetch = () => {
      const elapsed = Date.now() - lastFetchAtRef.current;
      if (elapsed >= REFETCH_DEBOUNCE_MS) {
        fetchSubscription();
      }
    };

    const onFocus = () => maybeRefetch();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefetch();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, fetchSubscription]);

  // Realtime - the billing webhook broadcasts on a per-user channel whenever
  // entitlement changes (own subscription, or the family owner's plan). The
  // `user_subscriptions` table is deliberately NOT in the realtime publication,
  // so this uses broadcast only and exposes no row data.
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`entitlement:${user.id}`)
      .on('broadcast', { event: 'refresh' }, () => {
        fetchSubscription();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscription]);

  // Safety net: poll while the tab is visible so a long-lived session can never
  // hold stale premium access if a broadcast is missed.
  useEffect(() => {
    if (!user) return;

    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      fetchSubscription();
    }, 5 * 60 * 1000);

    return () => window.clearInterval(interval);
  }, [user, fetchSubscription]);

  // Admins always get premium access
  if (isAdmin) {
    return {
      isPremium: true,
      isTrialActive: false,
      trialDaysLeft: 0,
      planType: 'premium' as const,
      cancelAtPeriodEnd: false,
      currentPeriodEnd: null,
      lsStatus: null,
      lsSubscriptionId: null,
      lsVariantId: null,
      trialStartedAt: null,
      trialEndsAt: null,
      hadTrial: false,
      loading: state.loading,
      fetchError: false,
      refetch: fetchSubscription,
    };
  }

  return {
    ...state,
    refetch: fetchSubscription,
  };
}
